import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import type { MatchFrame } from "@/types";

export interface SmartBonusContext {
  lastThirdPossession: number; // 0..1
  scoreState: string; // e.g. "0-0"
  matchMinute: number;
  attackingPressure: number; // 0..1
  xgSimulated: number;
  teamInAttack: "MEX" | "ARG" | null;
  userRegion: string;
  responsibleGamingAllowed: boolean;
}

export interface SmartBonusOffer {
  id: string;
  title: string;
  description: string;
  countdownSeconds: number;
  simulatedOdds: string;
  disclaimer: string;
  triggerReason: string;
  teamInAttack: "MEX" | "ARG" | null;
  matchId: string;
  city: string;
}

const COOLDOWN_MS = 10 * 60 * 1000; // 1 popup per 10 min
const PRESSURE_WINDOW_SEC = 5; // sustained pressure window (simulated)

interface EngineState {
  lastShownAt: number;
  consecutiveDismissals: number;
  pressureStartedAt: number | null;
  lastBonusId: string | null;
}

const state: EngineState = {
  lastShownAt: 0,
  consecutiveDismissals: 0,
  pressureStartedAt: null,
  lastBonusId: null,
};

export function evaluateSmartBonus(
  frame: MatchFrame,
  elapsed: number,
  ctx: { city: string; matchId: string; responsibleGamingAllowed: boolean },
): SmartBonusOffer | null {
  if (!ctx.responsibleGamingAllowed) return null;
  if (state.consecutiveDismissals >= 2) return null;
  if (Date.now() - state.lastShownAt < COOLDOWN_MS) return null;

  // Last-third possession: players from one team with x in last third
  const mexInLast = frame.players.filter((p) => p.team === "MEX" && p.x > 16).length;
  const argInLast = frame.players.filter((p) => p.team === "ARG" && p.x < -16).length;
  const mexShare = mexInLast / Math.max(1, mexInLast + argInLast);
  const argShare = 1 - mexShare;

  const dominantTeam: "MEX" | "ARG" | null =
    mexShare > 0.7 ? "MEX" : argShare > 0.7 ? "ARG" : null;
  const scoreState = `${frame.scoreMex}-${frame.scoreArg}`;
  const isGoalless = frame.scoreMex === 0 && frame.scoreArg === 0;

  if (!dominantTeam || !isGoalless) {
    state.pressureStartedAt = null;
    return null;
  }
  if (state.pressureStartedAt == null) {
    state.pressureStartedAt = elapsed;
    return null;
  }
  if (elapsed - state.pressureStartedAt < PRESSURE_WINDOW_SEC) return null;

  // Build offer
  const offer: SmartBonusOffer = {
    id: `bonus_${Date.now().toString(36)}`,
    title: "Gol Inminente · Bono Especial",
    description: "Alta presión ofensiva detectada. Bono disponible por tiempo limitado.",
    countdownSeconds: 60,
    simulatedOdds: dominantTeam === "MEX" ? "2.10" : "1.95",
    disclaimer: "Promoción sujeta a términos, elegibilidad y jurisdicción. Demo simulada.",
    triggerReason: `last_third_possession>${(Math.max(mexShare, argShare) * 100).toFixed(0)}%@${scoreState}`,
    teamInAttack: dominantTeam,
    matchId: ctx.matchId,
    city: ctx.city,
  };
  state.lastShownAt = Date.now();
  state.lastBonusId = offer.id;
  state.pressureStartedAt = null;

  void recordBonusEvent(offer, { clicked: false, expired: false });
  void trackEvent("smart_bonus_triggered", {
    bonus_id: offer.id,
    team_in_attack: offer.teamInAttack,
    score_state: scoreState,
    minute: frame.minute,
    trigger_reason: offer.triggerReason,
    city: ctx.city,
  });

  return offer;
}

export function noteBonusDismissed() {
  state.consecutiveDismissals += 1;
}

export function noteBonusClicked() {
  state.consecutiveDismissals = 0;
}

export async function recordBonusEvent(
  offer: SmartBonusOffer,
  patch: { clicked?: boolean; expired?: boolean },
) {
  try {
    await supabase.from("smart_bonus_events").insert({
      session_id: getSessionId(),
      tenant_id: "univision-demo",
      match_id: offer.matchId,
      bonus_type: "contextual_pressure",
      trigger_reason: offer.triggerReason,
      team_in_attack: offer.teamInAttack,
      city: offer.city,
      simulated_odds: offer.simulatedOdds,
      countdown_seconds: offer.countdownSeconds,
      clicked: patch.clicked ?? false,
      expired: patch.expired ?? false,
      metadata: { bonus_id: offer.id, title: offer.title } as never,
    });
  } catch (e) {
    console.warn("[smartBonus] insert failed", e);
  }
}

export function isResponsibleGamingAllowed(): boolean {
  if (typeof window === "undefined") return false;
  // Default: allowed in demo. Could be toggled per user_profile in future.
  const flag = localStorage.getItem("ss2026_rg_block");
  return flag !== "1";
}